import { useNavigate } from "@tanstack/react-router";
import { CheckCircle } from "lucide-react";

import type { Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
  deal: Deal;
  onStartOver?: () => void;
}

export function DealConfirmation({ deal, onStartOver }: Props) {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  // CM/RA/RM register on behalf via the manager routes; partners use their own.
  const isManager = user?.role === "channel_manager" || user?.role === "admin";
  const dashboardTo = isManager ? "/dashboard/manager/deals" : "/dashboard/partner/deals";
  const newDealTo = isManager ? "/dashboard/manager/deals/new" : "/dashboard/partner/deals/new";

  // CHP-300 Fix 3: a just-submitted deal is pending_review. Returning to the
  // partner dashboard pre-selects the "Pending Review" tab so the new deal is
  // immediately visible "in review" rather than buried under the default
  // "My Active" tab. (The manager ledger uses a different tab model — its
  // ledger components are owned by another lane — so we leave it untouched.)
  function returnToDashboard() {
    if (isManager) {
      void navigate({ to: dashboardTo });
    } else {
      void navigate({ to: dashboardTo, search: { tab: "pending" } });
    }
  }

  return (
    <div className="container py-6">
      <Card className="mx-auto max-w-lg">
        <CardHeader className="items-center text-center">
          <CheckCircle className="mb-2 h-12 w-12 text-success" aria-hidden />
          <CardTitle>Registration Received</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-center text-sm">
          <p>Thanks for submitting the opportunity with {deal.client_name}.</p>

          {deal.deal_id_display ? (
            <p>
              Deal reference: <span className="font-mono font-medium">{deal.deal_id_display}</span>
            </p>
          ) : null}

          {/* CHP-368: a CM/RA/RM registration IS the approval — the deal lands
              LIVE in the assigned partner's portfolio immediately. Partners
              still go through the 24-hour review queue. */}
          {isManager ? (
            <>
              <p className="text-muted-foreground">
                This deal is now live in the assigned partner's portfolio.
              </p>
              <p className="text-muted-foreground">Their 90-day protection window has started.</p>
            </>
          ) : (
            <>
              {/* CHP-809 / Flow B Confirmation & Next Steps — the labelled
                  "What happens next?" bullet list from the spec mock. CHP-611:
                  the email bullet below matches the submitting-partner
                  confirmation email the backend sends on submission. */}
              <p className="text-muted-foreground">
                Your registration is now marked{" "}
                <span className="font-medium text-foreground">In review</span>.
              </p>
              <div className="text-left">
                <h2 className="mb-2 text-center text-sm font-semibold">What happens next?</h2>
                <ul className="ml-5 list-disc space-y-1 text-muted-foreground">
                  <li>
                    Our team has been notified and your 24-hour review window has started — we'll
                    confirm the deal status within 24 hours.
                  </li>
                  <li>We'll update your dashboard when the evaluation is complete.</li>
                  <li>
                    A copy of this confirmation has been sent directly to your registered email
                    address.
                  </li>
                </ul>
              </div>
              <p className="text-muted-foreground">
                Your 90-day protection window begins upon approval.
              </p>
            </>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              onClick={() => {
                if (onStartOver) {
                  onStartOver();
                } else {
                  void navigate({ to: newDealTo });
                }
              }}
            >
              Register another deal
            </Button>
            <Button variant="outline" onClick={returnToDashboard}>
              Return to deals dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
